import {
  AggregatedServiceDto,
  CreateEnvironmentDto,
  EnvironmentDto,
  EnvironmentSearchRequestDto,
  PollingJobDto,
  ProcessingStatus,
  ProductDto,
  ServiceDto,
  UpdateEnvironmentDto,
} from '@onereach/billing-dto';
import { EnvironmentStatus, JobType, SortOrder } from '@onereach/billing-shared';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { Billing as billingApi } from '@/api';
import { TranslatableError } from '@/errors';
import type {
  IndexEntry as GIndexEntry,
  IndexMap as GIndexMap,
  PagingSortQuery,
  TableQueryParams,
} from '@/types/store';
import { isFresh, pruneLRU } from '@/utils/cache';
import { debouncePromise } from '@/utils/debounce';
import { hasEnvironmentService } from '@/utils/hasEnvironmentService';
import { hasExpiredAuthSession } from '@/utils/hasExpiredAuthSession';
import { makeEnvironmentsQueryKey } from '@/utils/queryKey';
import type { EnvironmentSelectFilters, EnvironmentSelectResult } from '@/widgets/EnvironmentSelect/types';

const CACHE_TTL_MS = 60_000;
const MAX_INDEXES = 50;

type FilterKeys = Exclude<keyof EnvironmentSearchRequestDto, 'page' | 'limit' | 'sortBy' | 'sortOrder'>;
export type EnvironmentsFilters = Partial<Pick<EnvironmentSearchRequestDto, FilterKeys>>;

export type EnvironmentsQuery = PagingSortQuery<EnvironmentsFilters, string, SortOrder | undefined>;

type EnvironmentsTableParams = TableQueryParams<EnvironmentsFilters, string, 'ASC' | 'DESC' | undefined>;

type EnvironmentIndexMeta = { total?: number };
type EnvironmentIndexEntry = GIndexEntry<string, EnvironmentIndexMeta>;


const useEnvironmentsStore = defineStore('environments', () => {
  const entities = ref<Record<string, EnvironmentDto>>({});
  const indexes = ref<GIndexMap<string, EnvironmentIndexMeta>>({});
  const loadingByKey = ref<Record<string, boolean>>({});
  const currentQuery = ref<EnvironmentsQuery>({
    page: 1,
    limit: 50,
  });
  const lastKnownTotal = ref(0);
  const inFlight = new Map<string, { promise: Promise<EnvironmentIndexEntry>; controller: AbortController }>();
  const isLoadingAny = computed(() => Object.values(loadingByKey.value).some(Boolean));
  const updatingEnvs = ref(new Set<string>());
  const updatingInFlight = new Map<string, AbortController>();

  // Legacy state for backward compatibility
  const isEnvironmentSaving = ref(false);
  const isEnvironmentDeleting = ref(false);
  const aggregatedServices = ref<AggregatedServiceDto[]>([]);
  const isAggregatedServicesLoading = ref(false);
  const registryProducts = ref<ProductDto[]>([]);
  const registryServices = ref<ServiceDto[]>([]);
  const isRegistryLoading = ref(false);
  const environmentScanJobs = ref<PollingJobDto[]>([]);
  const environmentScanJob = computed(() => environmentScanJobs.value[0] ?? null);
  const isStartingEnvironmentScan = ref(false);
  const activeEnvironmentScanStatuses = new Set<ProcessingStatus>([
    ProcessingStatus.QUEUED,
    ProcessingStatus.IN_PROGRESS,
  ]);
  const isEnvironmentScanActive = computed(() => (
    isStartingEnvironmentScan.value
    || environmentScanJobs.value.some(job => activeEnvironmentScanStatuses.has(job.status))
  ));

  function upsertEnvironmentScanJob(job: PollingJobDto) {
    environmentScanJobs.value = [
      job,
      ...environmentScanJobs.value.filter(currentJob => currentJob.jobId !== job.jobId),
    ];
  }

  const fetchAggregatedServices = async () => {
    isAggregatedServicesLoading.value = true;
    try {
      aggregatedServices.value = await billingApi.environmentsApi.getAggregatedServices();
    } catch (error) {
      console.error('Failed to fetch aggregated services', error);
      aggregatedServices.value = [];
    } finally {
      isAggregatedServicesLoading.value = false;
    }
  };

  const fetchRegistry = async () => {
    isRegistryLoading.value = true;
    try {
      const registry = await billingApi.discoveryApi.getRegistry();
      registryProducts.value = registry.products;
      registryServices.value = registry.services;
    } catch (error) {
      console.error('Failed to fetch discovery registry', error);
      registryProducts.value = [];
      registryServices.value = [];
    } finally {
      isRegistryLoading.value = false;
    }
  };

  const toEnumSort = (v?: 'ASC' | 'DESC' | SortOrder): SortOrder | undefined =>
    v ? (v as SortOrder) : undefined;

  // Debounced search for environment select widget
  const searchMinimalDebounced = debouncePromise(
    async (term, limit, filters) => {
      const termStr = term as string;
      const limitNum = limit as number;
      const filtersObj = filters as EnvironmentSelectFilters | undefined;

      const searchParams: EnvironmentSearchRequestDto = {
        search: termStr,
        limit: limitNum,
        status: filtersObj?.status,
      };

      // Add customer filtering if provided
      if (filtersObj?.customerIds?.length) {
        searchParams.customerId = filtersObj.customerIds;
      }

      // Add client filtering if provided
      if (filtersObj?.client) {
        searchParams.client = filtersObj.client;
      }

      // Add environment filtering if provided
      if (filtersObj?.environment) {
        searchParams.environment = filtersObj.environment;
      }

      const allowedTargets = filtersObj?.allowedTargets;
      const queries = allowedTargets
        ? allowedTargets.map(target => ({
          ...searchParams,
          client: target.client,
          environment: target.environment,
        }))
        : [searchParams];
      const responses = await Promise.all(queries.map(query => billingApi.environmentsApi.getEnvironmentsV2(query)));
      const list = [...new Map(responses
        .flatMap(response => (response.data || []) as EnvironmentDto[])
        .map(environment => [environment.id, environment] as const)).values()].slice(0, limitNum);

      // Keep search results in entities cache so select widgets can render labels
      // immediately after selection without an extra fetch.
      for (const env of list) {
        if (env?.id) entities.value[env.id] = env;
      }

      return list.map((env: EnvironmentDto) => ({
        id: env.id,
        friendlyName: env.friendlyName || '',
        client: env.client || '',
        customerId: env.customerId || '',
      }));
    },
    300,
  );

  function getList(query: EnvironmentsQuery) {
    const key = makeEnvironmentsQueryKey(query);
    const ids = indexes.value[key]?.ids ?? [];
    return ids
      .map((id) => entities.value[id])
      .filter((item): item is EnvironmentDto => item !== undefined);
  }

  function setQuery(input: EnvironmentsTableParams) {
    currentQuery.value = {
      ...(input.filters ?? {}),
      page: input.page,
      limit: input.limit,
      sortBy: input.sortBy,
      sortOrder: toEnumSort(input.sortOrder),
    };
  }

  async function syncCurrent(options?: { waitFresh?: boolean }) {
    if (options?.waitFresh) {
      await ensureIndexFresh(currentQuery.value);
    } else {
      void ensureIndex(currentQuery.value, { swr: true });
    }
  }

  async function hasDraftEnvironments(): Promise<boolean> {
    const response = await billingApi.environmentsApi.getEnvironmentsV2Unscoped({
      status: EnvironmentStatus.DRAFT,
      page: 1,
      limit: 1,
    });

    return (response.total ?? response.data?.length ?? 0) > 0;
  }

  async function ensureIndex(query: EnvironmentsQuery, opts: { swr?: boolean; force?: boolean } = {}) {
    const key = makeEnvironmentsQueryKey(query);
    const entry = indexes.value[key];
    const swr = opts.swr ?? true;
    if (!opts.force && entry && isFresh(entry.ts, CACHE_TTL_MS)) return entry as EnvironmentIndexEntry;
    if (swr && entry) {
      void fetchAndSetIndex(query);
      return entry as EnvironmentIndexEntry;
    }
    return fetchAndSetIndex(query);
  }

  async function ensureIndexFresh(query: EnvironmentsQuery) {
    return fetchAndSetIndex(query);
  }

  async function fetchNext(query: EnvironmentsQuery) {
    const nextPage = (query.page ?? 1) + 1;
    const nextQuery: EnvironmentsQuery = {
      ...query,
      page: nextPage,
    };
    return fetchAndMergeIndex(nextQuery);
  }

  async function fetchAndSetIndex(query: EnvironmentsQuery): Promise<EnvironmentIndexEntry> {
    const key = makeEnvironmentsQueryKey(query);
    if (inFlight.has(key)) return inFlight.get(key)!.promise;
    const controller = new AbortController();
    const promise = (async () => {
      try {
        loadingByKey.value[key] = true;
        const { page, limit, sortBy, sortOrder, ...restFilters } = query;
        const params = {
          ...(restFilters as EnvironmentsFilters),
          page: page ?? 1,
          limit: limit ?? 50,
          sortBy,
          sortOrder,
        };

        const data = await billingApi.environmentsApi.getEnvironmentsV2Unscoped(params);
        const list = (data.data ?? []) as EnvironmentDto[];

        for (const environment of list) entities.value[environment.id] = environment;
        const newEntry: EnvironmentIndexEntry = {
          ids: list.map((environment) => environment.id),
          total: data.total,
          ts: Date.now(),
        };
        (indexes.value as Record<string, EnvironmentIndexEntry>)[key] = newEntry;
        if (typeof newEntry.total === 'number') lastKnownTotal.value = newEntry.total;
        pruneLRU(indexes.value as Record<string, EnvironmentIndexEntry>, MAX_INDEXES);
        return newEntry;
      } finally {
        loadingByKey.value[key] = false;
        inFlight.delete(key);
      }
    })();
    inFlight.set(key, {
      promise,
      controller,
    });
    return promise;
  }

  async function fetchAndMergeIndex(nextQuery: EnvironmentsQuery) {
    const baseKey = makeEnvironmentsQueryKey({
      ...nextQuery,
      page: (nextQuery.page ?? 1) - 1,
    });
    const pageEntry = await fetchAndSetIndex(nextQuery);
    const baseEntry = indexes.value[baseKey] as EnvironmentIndexEntry | undefined;
    if (!baseEntry) return pageEntry;
    const merged: EnvironmentIndexEntry = {
      ids: [...baseEntry.ids, ...pageEntry.ids.filter((id) => !baseEntry.ids.includes(id))],
      total: pageEntry.total ?? baseEntry.total,
      ts: Date.now(),
    };
    (indexes.value as Record<string, EnvironmentIndexEntry>)[baseKey] = merged;
    if (typeof merged.total === 'number') lastKnownTotal.value = merged.total;
    pruneLRU(indexes.value as Record<string, EnvironmentIndexEntry>, MAX_INDEXES);
    return merged;
  }

  function invalidateAllIndexes() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    for (const key of Object.keys(indexes.value)) { // @ts-expect-error
      indexes.value[key].ts = 0;
    }
  }

  function getEnvironmentById(id: string): EnvironmentDto | undefined {
    return entities.value[id];
  }

  async function ensureEnvironmentLoaded(id: string): Promise<EnvironmentDto | undefined> {
    // 1. Check if already in entities cache
    let environment = entities.value[id];
    if (environment) {
      return environment;
    }

    // 2. If not cached, load from API
    try {
      environment = await loadEnvironmentById(id);
      return environment;
    } catch (error) {
      console.error(`Failed to load environment ${id}:`, error);
      return undefined;
    }
  }

  const pendingEnvironmentLoads = new Map<string, Promise<EnvironmentDto | undefined>>();

  async function loadEnvironmentById(id: string): Promise<EnvironmentDto | undefined> {
    const pending = pendingEnvironmentLoads.get(id);
    if (pending) return pending;

    const load = (async () => {
      const response = await billingApi.environmentsApi.getEnvironmentsV2({ id });
      const environment = response.data?.[0] as EnvironmentDto | undefined;
      if (environment) entities.value[environment.id] = environment;
      return environment;
    })();
    pendingEnvironmentLoads.set(id, load);
    try {
      return await load;
    } finally {
      pendingEnvironmentLoads.delete(id);
    }
  }

  function hasService(id: string | undefined, serviceKey: string): boolean {
    return hasEnvironmentService(id ? entities.value[id] : undefined, serviceKey);
  }

  const createEnvironment = async (data: CreateEnvironmentDto): Promise<EnvironmentDto> => {
    isEnvironmentSaving.value = true;
    try {
      const newEnvironment = await billingApi.environmentsApi.createEnvironment(data);
      entities.value[newEnvironment.id] = newEnvironment;
      invalidateAllIndexes();
      return newEnvironment;
    } catch (error) {
      console.error('Failed to create environment', error);
      throw new TranslatableError('errors.createEnvironment');
    } finally {
      isEnvironmentSaving.value = false;
    }
  };

  const updateEnvironment = async (id: string, data: UpdateEnvironmentDto): Promise<EnvironmentDto> => {
    isEnvironmentSaving.value = true;
    try {
      const updatedEnvironment = await billingApi.environmentsApi.updateEnvironment(id, data);
      entities.value[updatedEnvironment.id] = updatedEnvironment;
      invalidateAllIndexes();
      return updatedEnvironment;
    } catch (error) {
      console.error('Failed to update environment', error);
      throw new TranslatableError('errors.updateEnvironment');
    } finally {
      isEnvironmentSaving.value = false;
    }
  };

  const deleteEnvironment = async (id: string, softDelete: boolean = true): Promise<void> => {
    isEnvironmentDeleting.value = true;
    try {
      await billingApi.environmentsApi.deleteEnvironment(id, softDelete);
      delete entities.value[id];
      invalidateAllIndexes();
    } catch (error) {
      console.error('Failed to delete environment', error);
      throw new TranslatableError('errors.deleteEnvironment');
    } finally {
      isEnvironmentDeleting.value = false;
    }
  };

  const syncEnvironmentData = async (id: string): Promise<EnvironmentDto> => {
    try {
      await billingApi.environmentsApi.syncEnvironmentData(id);
      const environment = await loadEnvironmentById(id);
      if (!environment) throw new TranslatableError('errors.syncEnvironmentData');
      invalidateAllIndexes();
      return environment;
    } catch (error) {
      console.error('Failed to sync environment data', error);
      throw new TranslatableError('errors.syncEnvironmentData');
    }
  };

  async function startScanNewEnvironments(): Promise<PollingJobDto> {
    if (isEnvironmentScanActive.value && environmentScanJob.value) return environmentScanJob.value;
    if (isStartingEnvironmentScan.value) throw new TranslatableError('errors.scanNewEnvironments');

    isStartingEnvironmentScan.value = true;
    try {
      const jobId = await billingApi.environmentsApi.startScanNewEnvs();
      return await loadEnvironmentScanJobStatus(jobId);
    } catch (error) {
      console.error('Failed to start scanning new environments', error);
      throw new TranslatableError('errors.scanNewEnvironments');
    } finally {
      isStartingEnvironmentScan.value = false;
    }
  }

  async function loadActiveEnvironmentScanJobs(): Promise<PollingJobDto[]> {
    try {
      const response = await billingApi.pollingApi.getJobs({
        status: [ProcessingStatus.QUEUED, ProcessingStatus.IN_PROGRESS],
        jobType: JobType.ENVIRONMENT_SCAN,
      });
      environmentScanJobs.value = response.data;
      return response.data;
    } catch (error) {
      if (hasExpiredAuthSession()) {
        throw new DOMException('Polling stopped while the session is expired', 'AbortError');
      }

      console.error('Failed to load active environment scan jobs', error);
      throw new TranslatableError('errors.scanNewEnvironments');
    }
  }

  async function loadEnvironmentScanJobStatus(jobId: string): Promise<PollingJobDto> {
    try {
      const job = await billingApi.pollingApi.getJobStatus(jobId);
      upsertEnvironmentScanJob(job);
      if (!activeEnvironmentScanStatuses.has(job.status)) invalidateAllIndexes();
      return job;
    } catch (error) {
      if (hasExpiredAuthSession()) {
        throw new DOMException(`Polling stopped for job ${jobId}`, 'AbortError');
      }

      console.error('Failed to load environment scan job status', error);
      throw new TranslatableError('errors.scanNewEnvironments');
    }
  }

  // Computed properties for EnvironmentsDashboard
  const list = computed(() => getList(currentQuery.value));
  const isCurrentLoading = computed(() => {
    const key = makeEnvironmentsQueryKey(currentQuery.value);
    return !!loadingByKey.value[key];
  });
  const total = computed(() => {
    const key = makeEnvironmentsQueryKey(currentQuery.value);
    const idx = indexes.value[key] as EnvironmentIndexEntry | undefined;
    return typeof idx?.total === 'number' ? idx.total : lastKnownTotal.value;
  });

  // Public method for environment select widget
  async function searchForSelect(term: string, limit = 50, filters?: EnvironmentSelectFilters): Promise<EnvironmentSelectResult[]> {
    try {
      return await searchMinimalDebounced(term, limit, filters);
    } catch (e: unknown) {
      if (e && typeof e === 'object' && (e as { message?: string }).message === 'Debounced') {
        return [];
      }
      throw e;
    }
  }

  const updateEnvironmentDiscoveryData = async (environmentId: string): Promise<void> => {
    if (updatingInFlight.has(environmentId)) {
      updatingInFlight.get(environmentId)?.abort();
    }

    const controller = new AbortController();
    updatingInFlight.set(environmentId, controller);
    updatingEnvs.value = new Set(updatingEnvs.value).add(environmentId);

    try {
      const discoveryData = await billingApi.discoveryApi.updateEnvironmentDiscoveryData(environmentId, { signal: controller.signal });
      // Update the environment in our local cache with the new discovery data
      const environment = entities.value[environmentId];
      if (environment) {
        entities.value[environmentId] = {
          ...environment,
          discoveryData,
          discoveryLastUpdated: discoveryData.discoveryLastUpdated,
        };
      }
      // Invalidate indexes to force refresh on next query
      invalidateAllIndexes();
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((error as any).name === 'AbortError') return;
      console.error('Failed to update environment discovery data:', error);
      throw new TranslatableError('errors.updateEnvironmentDiscoveryData');
    } finally {
      if (updatingInFlight.get(environmentId) === controller) {
        updatingInFlight.delete(environmentId);
      }
      const nextSet = new Set(updatingEnvs.value);
      nextSet.delete(environmentId);
      updatingEnvs.value = nextSet;
    }
  };

  const getServiceVersionJson = async (environmentId: string, url: string): Promise<Record<string, unknown>> => {
    try {
      return await billingApi.discoveryApi.getServiceVersionJson(environmentId, url) as Promise<Record<string, unknown>>;
    } catch (error) {
      console.error('Failed to fetch version.json', error);
      throw error;
    }
  };

  return {
    // Core state
    entities,
    indexes,
    loadingByKey,
    currentQuery,

    // Query interface
    getList,
    ensureIndex,
    ensureIndexFresh,
    fetchNext,
    setQuery,
    syncCurrent,
    hasDraftEnvironments,
    isLoadingAny,

    // Computed properties
    total,
    list,
    isCurrentLoading,

    // Business methods
    searchForSelect,
    getEnvironmentById,
    ensureEnvironmentLoaded,
    loadEnvironmentById,
    hasService,
    createEnvironment,
    updateEnvironment,
    deleteEnvironment,
    syncEnvironmentData,
    startScanNewEnvironments,
    loadActiveEnvironmentScanJobs,
    loadEnvironmentScanJobStatus,
    updateEnvironmentDiscoveryData,
    fetchAggregatedServices,
    fetchRegistry,
    getServiceVersionJson,

    // Aggregated services state
    aggregatedServices,
    isAggregatedServicesLoading,
    updatingEnvs,

    // Discovery registry state
    registryProducts,
    registryServices,
    isRegistryLoading,

    // Environment-specific properties
    isEnvironmentSaving,
    isEnvironmentDeleting,
    environmentScanJob,
    environmentScanJobs,
    isEnvironmentScanActive,
  };
});

export default useEnvironmentsStore;
